// Story 4.8 — TanStack Query mutation hook for POST /api/v1/content/flag.
//
// Mirrors Story 4.5's useChangeHandle.ts conventions:
//   * Generates the JSON payload from the variables object.
//   * Calls attachRequestIdToScope() for Sentry correlation per Story 1.8.
//   * On non-OK: parses { error: { code, message, field? } } shape +
//     throws so consumers can `.catch` or read useMutation `.error`.
//   * NO `onSuccess` cache invalidation — the flag submission doesn't
//     change any SPA-side cached data (the row lands in the admin
//     moderation queue, which the SPA doesn't read).
//
// Typed error shapes the consumer cares about:
//   * 400 VALIDATION_FAILED on field `target_kind` | `reason` —
//     client-side validation should prevent this, but defensive.
//   * 429 RATE_LIMITED — visitor has flagged 20 in the last 24h.
//   * 401 — should be caught by session middleware on the SERVER
//     side branching (anonymous accepted, no 401), but a stale
//     cookie scenario could surface this; the hook propagates as
//     typed error.

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type FlagContentTargetKind =
  | "creator_image"
  | "hello_text"
  | "share_certificate"
  | "bulletin_post";

export interface FlagContentVariables {
  target_kind: FlagContentTargetKind;
  target_id: string;
  reason?: string;
}

export interface FlagContentResponseData {
  status: string; // "received" — stable across new-insert vs dedup
  flag_id: string;
  review_eta_hours: number;
  created_at: string;
}

export interface FlagContentError {
  code: string;
  message: string;
  field?: string;
  status: number;
}

async function postFlagContent(
  variables: FlagContentVariables,
): Promise<FlagContentResponseData> {
  const response = await fetch("/api/v1/content/flag", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(variables),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: FlagContentError = {
      code: "FLAG_CONTENT_FAILED",
      message: `Flag submission failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep the default error shape.
    }
    throw detail;
  }
  const body = (await response.json()) as { data: FlagContentResponseData };
  return body.data;
}

export function useFlagContent(): UseMutationResult<
  FlagContentResponseData,
  FlagContentError,
  FlagContentVariables
> {
  return useMutation<FlagContentResponseData, FlagContentError, FlagContentVariables>({
    mutationFn: postFlagContent,
    // NO onSuccess — flag submission doesn't mutate any client-side
    // cached data the SPA reads.
  });
}
